import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    GetHomeBannerList,
    AddHomeBanner,
    UpdateHomeBanner,
} from "../../Redux/Actions/homeBannerAction";
import { columnsHomeBannerList } from "../../Global Functions/columnsAntd";
import TableComponent from "../../Global Functions/tableComponents";
import toast from "react-hot-toast";
import HomeBannerEditModal from "./addAndEditHomeBanner";
import HomeBannerViewModal from "./viewHomeBanner";
import DateRangePopup from "../../Utils/dateFilterPopup";
import { Button } from "antd";

const ListHomeBanner = () => {
    const dispatch = useDispatch();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedBanner, setSelectedBanner] = useState(null);

    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [selectedViewBanner, setSelectedViewBanner] = useState(null);

    const [searchQuery, setSearchQuery] = useState("");
    const [isDatePopupVisible, setIsDatePopupVisible] = useState(false);
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ["5", "10", "20", "50", "100"],
    });

    const { admin } = useSelector((state) => state.admin || {});
    const accessCode = admin?.data?.access_code || [];
    const canAdd = Array.isArray(accessCode) && accessCode.includes("add");
    const canEdit = Array.isArray(accessCode) && accessCode.includes("edit");

    const fetchDetails = useCallback(
        (showLoader = true, page = pagination.current, pageSize = pagination.pageSize) => {
            const payload = {
                page,
                limit: pageSize,
                filter: debouncedSearch,
                is_deleted: false,
                ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
                ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
            };
            return dispatch(GetHomeBannerList(payload, showLoader)).catch(() => {
                if (showLoader) {
                    toast.error("Failed to fetch home banner list");
                }
            });
        },
        [dispatch, debouncedSearch, dateRange, pagination.current, pagination.pageSize]
    );

    const homeBannerState = useSelector((state) => state.homeBanner || {});
    const banners = useMemo(() => {
        return homeBannerState.banners || [];
    }, [homeBannerState.banners]);
    const bannerTotal = useMemo(() => {
        return homeBannerState.total || 0;
    }, [homeBannerState.total]);
    const { loading } = homeBannerState;

    useEffect(() => {
        fetchDetails(true, pagination.current, pagination.pageSize);
    }, [pagination.current, pagination.pageSize, fetchDetails]);

    useEffect(() => {
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, [debouncedSearch, dateRange]);

    useEffect(() => {
        setPagination((prev) => ({ ...prev, total: bannerTotal }));
    }, [bannerTotal]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    const openEditModal = useCallback((banner = null) => {
        setSelectedBanner(banner);
        setIsModalOpen(true);
    }, []);

    const closeEditModal = () => {
        setSelectedBanner(null);
        setIsModalOpen(false);
    };

    const openViewModal = useCallback((banner = null) => {
        setSelectedViewBanner(banner);
        setIsViewModalOpen(true);
    }, []);

    const closeViewModal = () => {
        setSelectedViewBanner(null);
        setIsViewModalOpen(false);
    };

    const handleSubmit = async (values) => {
        try {
            let result;
            if (selectedBanner?._id) {
                result = await dispatch(UpdateHomeBanner({ ...values, id: selectedBanner._id }));
            } else {
                result = await dispatch(AddHomeBanner(values));
            }

            if (result) {
                await fetchDetails(false, pagination.current, pagination.pageSize);
                closeEditModal();
                toast.success(selectedBanner?._id ? "Banner updated successfully" : "Banner added successfully");
                return { success: true };
            }
        } catch (error) {
            toast.error("Failed to save banner");
            return { success: false };
        }
    };

    const handleDateRangeApply = (start, end) => {
        if (start === null && end === null) {
            setDateRange({ start: null, end: null });
        } else {
            setDateRange({ start, end });
        }
        setIsDatePopupVisible(false);
    };

    const handleTableChange = (newPagination) => {
        setPagination(newPagination);
    };

    return (
        <>
            <div style={{ position: "absolute", marginTop: "9.6%", marginLeft: "88.5%" }}>
                {canAdd && (
                    <Button
                        style={{ background: "#070C18", color: "white", padding: "18px", borderRadius: "20px" }}
                        onClick={() => openEditModal(null)}
                    >
                        Add Banner
                    </Button>
                )}
            </div>

            <TableComponent
                pagination={pagination}
                setState={setSearchQuery}
                dataSource={banners}
                onCalendarClick={() => setIsDatePopupVisible(true)}
                onChange={handleTableChange}
                h3heading="Home Banner"
                columns={columnsHomeBannerList(
                    pagination.current,
                    pagination.pageSize,
                    openViewModal,
                    openEditModal,
                    canEdit
                )}
                loading={loading}
            />

            {isModalOpen && (
                <HomeBannerEditModal
                    visible={isModalOpen}
                    bannerData={selectedBanner}
                    onSave={handleSubmit}
                    onClose={closeEditModal}
                />
            )}

            {isViewModalOpen && (
                <HomeBannerViewModal
                    visible={isViewModalOpen}
                    bannerData={selectedViewBanner}
                    onClose={closeViewModal}
                />
            )}

            <DateRangePopup
                visible={isDatePopupVisible}
                initialStart={dateRange.start}
                initialEnd={dateRange.end}
                onCancel={() => setIsDatePopupVisible(false)}
                onOk={handleDateRangeApply}
            />
        </>
    );
};

export default ListHomeBanner;